// deps
import React, { ReactElement, memo, useContext, useMemo, useCallback, ReactNode, useState } from "react";

// internal-deps
import {
    ApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    CheckoutRoute,
    getUserFromUserContextValue,
    PublicApiContext,
    useNavigate,
    useOnBack,
    UserContext,
} from "@swiggy-private/checkout-core";
import { Some } from "@swiggy-private/js-fp";
import { LinkHalfCard, PayLaterLinkedData, FullPageLoader } from "@swiggy-private/payment-ui";
import { captureError, useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";

// constants
import { LAZY_PAY_META } from "@payment-client/constants/Options";
import { LINK_LAZYPAY, OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// components
import PaymentModesWrapper from "@payment-client/components/payment-modes/components/payment-modes-wrapper/PaymentModesWrapper";
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";

// helpers
import { fetchPaylaterLinkStatus } from "@payment-client/helpers/PaylaterHelper";
import { showNativeAlert } from "@payment-client/utils/Native";

// styles
import styles from "./styles.scss";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

interface IPaylaterFullPageProps extends RouteProps<{}> {
    isPaymentModeRoute?: boolean;
}

const LinkPaylaterFullPageWrapper = function LinkPaylaterFullPageWrapper(props: {
    children: ReactNode;
    isPaymentModeRoute: boolean;
}): ReactElement {
    const { children, isPaymentModeRoute } = props;

    if (isPaymentModeRoute) {
        return (
            <PaymentModesWrapper containerClassName={styles.container} title={LINK_LAZYPAY}>
                {children}
            </PaymentModesWrapper>
        );
    }

    const header = <CommonHeaderContent title={LINK_LAZYPAY} />;
    return <CommonUIWrapper containerCSSClass={styles.container} header={header} content={children} />;
};

function LinkPaylaterFullPage({ isPaymentModeRoute = false }: IPaylaterFullPageProps): ReactElement | null {
    const { user: userContextValue } = useContext(UserContext);
    const [apiCallInProgress, setApiCallInProgress] = useState(false);
    const onBack = useOnBack();
    const isMountedRef = useMountedRef();
    const [paylaterState, setPaylaterState] = useState<PayLaterLinkedData>();
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);
    const navigateTo = useNavigate();
    const publicApiConfig = useContext(PublicApiContext);

    const _isPublicApiEnabled = useMemo(
        () =>
            isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYLATER_APIS,
                LAZY_PAY_META.paymentCode,
            ),
        [publicApiConfig],
    );

    useEffectOnce(() => {
        setApiCallInProgress(true);
        fetchPaylaterLinkStatus(
            apiHost,
            {
                name: LAZY_PAY_META.name,
                paymentCode: LAZY_PAY_META.paymentCode,
            },
            undefined,
            _isPublicApiEnabled,
        )
            .then(payload => {
                setApiCallInProgress(false);
                const res = payload.response;
                if (Some(res)) {
                    setPaylaterState(res.unwrap());
                }
            })
            .catch(err => {
                setApiCallInProgress(false);
                captureError(err);
                isMountedRef.current && showNativeAlert({ message: OOPS_SOMETHING_WENT_WRONG });
            });
    });

    const onProceed = useCallback(
        (payLaterCode: string) => {
            navigateTo(CheckoutRoute.LINK_PAYLATER, {
                payLaterCode,
                placeOrderOnSuccess: !isPaymentModeRoute,
                mobileNumber: user?.mobile,
            });
        },
        [isPaymentModeRoute, navigateTo, user],
    );

    /*
     * 1. Loading State
     * 2. Error State
     * 3. Successful API res: linked user
     * 4. Successful API res: non linked user
     */

    if (apiCallInProgress) {
        return <FullPageLoader />;
    }

    if (paylaterState == null || user == null) {
        return null;
    }

    if (paylaterState.linked) {
        onBack();
        return null;
    }

    return (
        <LinkPaylaterFullPageWrapper isPaymentModeRoute={isPaymentModeRoute}>
            <LinkHalfCard
                user={user}
                methodMeta={LAZY_PAY_META}
                payLaterLinkedData={paylaterState}
                onProceedToLink={onProceed}
                showAsFullPage
            />
        </LinkPaylaterFullPageWrapper>
    );
}
export default memo(LinkPaylaterFullPage);
